import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, AxiosStatic } from 'axios';

import { ISillyLogger } from './logger';
import { profilePromise } from './profile';
import { IRetryParams, isRetryParams, retryOnFail } from './retry-on-fail';
import { ITimeoutParams, requestAndRetry } from './timeout';

export interface IRequest {
  setUserAgentHeader(userAgentVal: string);

  get(uri: string, options?: AxiosRequestConfig): Promise<any>;

  post(uri: string, options?: AxiosRequestConfig, content?: any): Promise<any>;

  put(uri: string, options?: AxiosRequestConfig, content?: any): Promise<any>;

  patch(uri: string, options?: AxiosRequestConfig, content?: any): Promise<any>;

  head(uri: string, options?: AxiosRequestConfig): Promise<any>;

  delete(uri: string, options?: AxiosRequestConfig): Promise<any>;

  json<T>(uri: string, options?: AxiosRequestConfig): Promise<T>;

  send<T>(
    uri: string,
    requestConfig?: AxiosRequestConfig,
    retryParams?: ITimeoutParams | IRetryParams,
  ): Promise<AxiosResponse<T>>;
}

export class Request implements IRequest {
  constructor(private axiosInstance: AxiosStatic = axios,
              private logger?: ISillyLogger,
              private profileNetworkRequests?: boolean) {
  }

  public async setUserAgentHeader(userAgentVal: string): Promise<any> {
    this.axiosInstance.defaults.headers.common['User-Agent'] = userAgentVal;
  }

  public async get(uri: string, options: AxiosRequestConfig): Promise<any> {
    return (await profilePromise(
      {
        method: 'get',
        uri: this.axiosInstance.getUri({ url: uri, ...options }),
        promise: this.axiosInstance.get(uri, options),
        logger: this.logger,
        shouldLog: this.profileNetworkRequests,
      },
    )).data;
  }

  public async post(uri: string, options: AxiosRequestConfig, content: any): Promise<any> {
    return (await profilePromise(
      {
        method: 'post',
        uri: this.axiosInstance.getUri({ url: uri, ...options }),
        promise: this.axiosInstance.post(uri, content, options),
        logger: this.logger,
        shouldLog: this.profileNetworkRequests,
      })).data;
  }

  public async put(uri: string, options: AxiosRequestConfig, content: any): Promise<any> {
    return (await profilePromise(
      {
        method: 'put',
        uri: this.axiosInstance.getUri({ url: uri, ...options }),
        promise: this.axiosInstance.put(uri, content, options),
        logger: this.logger,
        shouldLog: this.profileNetworkRequests,
      },
    )).data;
  }

  public async patch(uri: string, options: AxiosRequestConfig, content: any): Promise<any> {
    return (await profilePromise(
      {
        method: 'patch',
        uri: this.axiosInstance.getUri({ url: uri, ...options }),
        promise: this.axiosInstance.patch(uri, content, options),
        logger: this.logger,
        shouldLog: this.profileNetworkRequests,
      },
    )).data;
  }

  public async head(uri: string, options: AxiosRequestConfig): Promise<any> {
    return (await profilePromise(
      {
        method: 'head',
        uri: this.axiosInstance.getUri({ url: uri, ...options }),
        promise: this.axiosInstance.head(uri, options),
        logger: this.logger,
        shouldLog: this.profileNetworkRequests,
      },
    )).data;
  }

  public async delete(uri: string, options: AxiosRequestConfig): Promise<any> {
    return (await profilePromise(
      {
        method: 'delete',
        uri: this.axiosInstance.getUri({ url: uri, ...options }),
        promise: this.axiosInstance.delete(uri, options),
        logger: this.logger,
        shouldLog: this.profileNetworkRequests,
      },
    )).data;
  }

  public async json<T>(uri: string, options: AxiosRequestConfig = {}): Promise<T> {
    return (await profilePromise(
      {
        method: options.method || 'get',
        uri: this.axiosInstance.getUri({ url: uri, ...options }),
        promise: this.axiosInstance.request({
          ...options,
          responseType: 'json',
          url: uri,
        }),
        logger: this.logger,
        shouldLog: this.profileNetworkRequests,
      },
    )).data as T;
  }

  public async send<T>(
    uri: string,
    requestConfig: AxiosRequestConfig = {},
    retryParams: ITimeoutParams | IRetryParams = {},
  ): Promise<AxiosResponse<T>> {
    const makeRequest = async () => {
      return profilePromise(
        {
          method: requestConfig.method || 'get',
          uri: this.axiosInstance.getUri({ url: uri, ...requestConfig }),
          promise: this.axiosInstance
            .request<T>({
              ...requestConfig,
              responseType: 'json',
              url: uri,
            }),
          logger: this.logger,
          shouldLog: this.profileNetworkRequests,
        },
      );
    };
    const tryRequest = async () => {
      let error: AxiosError = null;
      const result = await makeRequest().catch((err: AxiosError) => {
        error = err;
        return null;
      });
      return { result, error };
    };

    if (isRetryParams(retryParams)) {
      return await retryOnFail(makeRequest, retryParams);
    } else {
      return await requestAndRetry(tryRequest, retryParams.retries, uri);
    }
  }
}
